import * as React from 'react';
import cn from 'classnames';
import { Checkbox } from '@cvent/carina/components/Checkbox';
import { SearchIcon } from '@cvent/carina/components/Icon';
import { Search } from '@weddingspot/ws-api-client';

import { ContentCollapsible, ContentPersistent } from '../input-group-panel';
import { InputGroupPanel } from './InputGroupPanel';

import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';
import { LogPerformance } from './utils';
import { Field, FieldProps } from 'formik';
import { useDebouncedCallback } from 'use-debounce/lib';
import { FeatureFlagContext, VENUE_STYLE_AND_WAFFLE_FLAG_MAPPING_DICT } from '@weddingspot/ws-core-components';
const _styles = require('./StylesFilters.scss');

interface StylesFilterChoices {
    styles: Search.StylesFilterChoices;
    collapsibleSubsections: boolean;
}

type SearchMatch<T> = T & {
    styleIdx?: number; // original index of the style before filtering
    matchIdx?: number;
    matchOffset?: number;
};

const NUM_CHECKBOX_ROWS_VISIBLE_DEFAULT = 3;

/**
 * Given an input string and a bold index and offset, return a ReactNode that renders
 * the string with the target section bolded
 *
 * @param input the string to find the bold text in
 * @param boldIdx the start of the bolded substring
 * @param boldOffset the length of the bolded substring
 */
const highlightText = (input: string, boldIdx?: number, boldOffset?: number) => {
    if (boldIdx === undefined || boldOffset === undefined || boldIdx === -1) {
        return input;
    }

    const preSubstring = input.slice(0, boldIdx);
    const substring = input.substring(boldIdx, boldIdx + boldOffset);
    const postSubstring = input.slice(boldIdx + boldOffset, input.length);

    return (
        <>
            {preSubstring}
            <b>{substring}</b>
            {postSubstring}
        </>
    );
};

const StyleCheckbox = (props: { style: SearchMatch<Search.StylesFilterChoice>; hidden: boolean }) => {
    const { style, hidden } = props;
    const fieldName = `styles.${style.value}`;

    return (
        <Field name={fieldName}>
            {(props: FieldProps) => {
                const { field, form } = props;
                // We need to omit the value prop from filed before passing this down from
                // checkbox since semantically they aren't the same thing (i.e. HTML checkbox value attribute
                // shouldn't change like the formik field value)
                const { value, onChange, ..._field } = field;
                const checked = form.values.styles[style.value];

                if (style.label === 'Historic/Landmark Building') {
                    LogPerformance('renderCheckbox', form.values.styles);
                }

                return (
                    <div className={cn(_styles.checkboxWrapper, { [_styles.filteredOut]: hidden })}>
                        <Checkbox
                            {..._field}
                            id={fieldName}
                            checked={value}
                            onChange={(e) => {
                                onChange(e);
                                logEvent(AnalyticsEvent.EVTNM_CLICKED_FILTER, {
                                    Type: 'Styles',
                                    Label: style.label,
                                    Value: style.value,
                                    Checked: !checked,
                                    'SRP Version': 2,
                                });
                            }}
                        >
                            {highlightText(style.label, style.matchIdx, style.matchOffset)}
                        </Checkbox>
                    </div>
                );
            }}
        </Field>
    );
};

const pubSubData = {};
export const pubSubStylesInput = {
    subscribe: (name: string, callback: () => any) => {
        pubSubData[name] = callback;
        return () => {
            pubSubData[name] = null; // unsubscribe function
        };
    },
    fire: (name: string) => {
        if (pubSubData[name]) {
            pubSubData[name]();
        }
    },
};

export const StylesFilters = (props: StylesFilterChoices) => {
    const { styles, collapsibleSubsections } = props;
    const [searchInternal, setSearchInternal] = React.useState('');
    // Use search internal to immidiatly update input value,
    // but debounce search for update checkbox
    const [search, setSearch] = React.useState('');
    const [setSearchDebounce] = useDebouncedCallback(setSearch, 250);

    React.useEffect(() => {
        return pubSubStylesInput.subscribe('resetFilter', () => {
            setSearchInternal('');
            setSearch('');
        });
    }, []);

    // Group styles by category
    const categories = ['Aesthetic', 'Scenery', 'Venue Type'];
    const stylesByCategory: { [K: string]: Search.StylesFilterChoice[] } = {};
    categories.map((cat) => (stylesByCategory[cat] = []));
    styles.options.map((option) => stylesByCategory[option.category].push(option));
    const { flagEnabled } = React.useContext(FeatureFlagContext);

    return (
        <>
            <div className={_styles.searchInputContainer}>
                <div className={_styles.searchIcon}>
                    <SearchIcon color='#B7B8B9' size='s' />
                </div>
                <input
                    className={_styles.searchInput}
                    type='text'
                    value={searchInternal}
                    placeholder='Filter venue styles'
                    onChange={(e) => {
                        setSearchInternal(e.target.value);
                        setSearchDebounce(e.target.value);
                    }}
                    aria-label='Filter by venue styles'
                />
            </div>

            {categories.map((cat, idx) => {
                const catOptions = stylesByCategory[cat];

                // Filter the category options if search term is entered
                let filteredOptions: SearchMatch<Search.StylesFilterChoice>[] = [...catOptions];
                if (!!search) {
                    filteredOptions = filteredOptions
                        .map((opt, originalIdx) => {
                            const matchIdx = opt.label.toLowerCase().indexOf(search.toLowerCase());
                            return {
                                ...opt,
                                styleIdx: originalIdx,
                                matchIdx: matchIdx,
                                matchOffset: search.length,
                            };
                        })
                        .filter((opt) => opt.matchIdx > -1);
                }

                // Don't render section if there are no results
                if (filteredOptions.length === 0) {
                    return;
                }

                const collapsible = collapsibleSubsections && filteredOptions.length > NUM_CHECKBOX_ROWS_VISIBLE_DEFAULT && !search;
                const sliceIdx = collapsible
                    ? filteredOptions[filteredOptions.length - 1]?.styleIdx || NUM_CHECKBOX_ROWS_VISIBLE_DEFAULT
                    : catOptions.length;

                return (
                    <InputGroupPanel key={idx} title={cat} collapsible={collapsible} defaultCollapsed={collapsible}>
                        <ContentPersistent>
                            {catOptions.slice(0, sliceIdx).map((style, idx) => {
                                const processedStyle = filteredOptions.find((o) => o.value === style.value);
                                if (
                                    VENUE_STYLE_AND_WAFFLE_FLAG_MAPPING_DICT[style.label] == undefined ||
                                    !flagEnabled(VENUE_STYLE_AND_WAFFLE_FLAG_MAPPING_DICT[style.label])
                                ) {
                                    return (
                                        <StyleCheckbox
                                            key={idx}
                                            style={processedStyle || style}
                                            hidden={!!filteredOptions.length && !processedStyle}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </ContentPersistent>
                        <ContentCollapsible>
                            {() => {
                                return (
                                    <>
                                        {catOptions.slice(sliceIdx).map((style, idx) => {
                                            const processedStyle = filteredOptions.find((o) => o.value === style.value);
                                            if (
                                                VENUE_STYLE_AND_WAFFLE_FLAG_MAPPING_DICT[style.label] == undefined ||
                                                !flagEnabled(VENUE_STYLE_AND_WAFFLE_FLAG_MAPPING_DICT[style.label])
                                            ) {
                                                return (
                                                    <StyleCheckbox
                                                        key={idx}
                                                        style={processedStyle || style}
                                                        hidden={!!filteredOptions.length && !processedStyle}
                                                    />
                                                );
                                            }
                                            return null;
                                        })}
                                    </>
                                );
                            }}
                        </ContentCollapsible>
                    </InputGroupPanel>
                );
            })}
        </>
    );
};
